/**
 * "Efficiency" on a run (G18): the daemon's end-of-run findings (wrong model tier, blind
 * exploration, repeated full validation, serial independent work) with their evidence, and the
 * run's tokens by model tier. One labelled row; tap or click to see everything. The findings
 * arrive inside the run's encrypted usage report, so the hub never sees them.
 */
import type { RunEfficiency } from '@viko/protocol'

const TIER_LABEL = [
  ['fast', 'Fast tier'],
  ['standard', 'Standard tier'],
  ['deep', 'Deep tier'],
  ['unknown', 'Unknown tier'],
] as const

export function efficiencySummary(e: RunEfficiency): string {
  const n = e.findings.length
  if (!n) return 'No issues'
  const saving = e.findings.reduce((a, f) => a + (f.estSavingTokens ?? 0), 0)
  return `${n.toLocaleString()} ${n === 1 ? 'finding' : 'findings'}${
    saving > 0 ? ` · est. ${saving.toLocaleString()} tokens saveable` : ''
  }`
}

export function EfficiencyRow({ efficiency }: { efficiency: RunEfficiency | undefined }) {
  if (!efficiency) return null
  return (
    <details className="px-4 py-1 text-[14px]" data-testid="efficiency-row">
      <summary className="min-h-11 cursor-pointer py-2 text-text-2">
        <span className="font-medium text-text-1">Efficiency</span>
        {' · '}
        <span data-testid="efficiency-summary">{efficiencySummary(efficiency)}</span>
      </summary>
      <div className="pb-2 text-text-2">
        {efficiency.findings.length === 0 ? (
          <p className="m-0 pb-2">Nothing wasteful was found in this run.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0 pb-2">
            {efficiency.findings.map((f) => (
              <li key={`${f.id}-${f.title}`} data-testid={`efficiency-finding-${f.id}`}>
                <p className="m-0 font-medium text-text-1">{f.title}</p>
                <p className="m-0">{f.detail}</p>
                <p className="m-0">
                  <span className="font-medium">Suggestion: </span>
                  {f.suggestion}
                </p>
                {f.estSavingTokens !== undefined ? (
                  <p className="m-0">Est. saving: {f.estSavingTokens.toLocaleString()} tokens</p>
                ) : null}
                {f.evidence.length ? (
                  <ul className="m-0 list-none p-0" aria-label="Evidence">
                    {f.evidence.map((ev) => (
                      <li
                        key={`${ev.kind}-${ev.ref}-${ev.at ?? ''}`}
                        className="break-words font-mono"
                      >
                        {ev.kind === 'command'
                          ? 'Command'
                          : ev.kind === 'task'
                            ? 'Task'
                            : 'Subagent'}
                        {': '}
                        {ev.ref}
                        {ev.at !== undefined ? ` · ${new Date(ev.at).toLocaleTimeString()}` : ''}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4" aria-label="Tokens by tier">
          {TIER_LABEL.map(([k, label]) => (
            <div key={k} className="contents">
              <dt>{label}</dt>
              <dd className="m-0">{efficiency.tokensByTier[k].toLocaleString()} tokens</dd>
            </div>
          ))}
        </dl>
      </div>
    </details>
  )
}
